import React, { useEffect, useState } from "react";
import {
  CloudSun, Sun, Cloud, CloudFog, CloudDrizzle, CloudRain,
  CloudSnow, CloudLightning, ArrowUp, ArrowDown, MapPin,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// WMO weather code -> lucide icon.
const iconFor = (code) => {
  if (code == null) return CloudSun;
  if (code === 0) return Sun;
  if (code === 1 || code === 2) return CloudSun;
  if (code === 3) return Cloud;
  if (code === 45 || code === 48) return CloudFog;
  if (code >= 51 && code <= 57) return CloudDrizzle;
  if (code >= 61 && code <= 67) return CloudRain;
  if ((code >= 71 && code <= 77) || code === 85 || code === 86) return CloudSnow;
  if (code >= 80 && code <= 82) return CloudRain;
  if (code >= 95) return CloudLightning;
  return CloudSun;
};

const DAYS_NL = ["zo", "ma", "di", "wo", "do", "vr", "za"];

const dayLabel = (dateStr, index) => {
  if (index === 0) return "Vandaag";
  const d = new Date(`${dateStr}T12:00:00`);
  if (Number.isNaN(d.getTime())) return "";
  return DAYS_NL[d.getDay()];
};

const WeatherWidget = () => {
  const [w, setW] = useState(null);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const res = await fetch(`${API}/weather`);
        if (!res.ok) return;
        const data = await res.json();
        if (active && data && data.temp_max != null) setW(data);
      } catch (e) { /* ignore */ }
    };
    load();
    const id = setInterval(load, 60 * 60 * 1000);
    return () => { active = false; clearInterval(id); };
  }, []);

  if (!w) return null;
  const Icon = iconFor(w.code);
  const days = (w.daily || []).slice(0, 7); // whole week: today + next 6

  return (
    <section className="pt-2 pb-10" data-testid="weather-widget">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <div className="mb-4 flex items-center gap-2">
          <MapPin className="h-4 w-4 text-white/80" />
          <span className="text-sm font-bold uppercase tracking-widest text-white/80">
            Het weer in Nijmegen
          </span>
        </div>

        <div className="relative overflow-hidden rounded-3xl bg-[#4288F5] border border-white/15 shadow-[0_18px_40px_-22px_rgba(8,30,80,0.5)]">
          {/* Current conditions */}
          <div className="flex flex-wrap items-center justify-between gap-4 px-5 py-4 sm:px-6 sm:py-5">
            <div className="flex items-center gap-4">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white/15 ring-1 ring-white/25">
                <Icon className="h-7 w-7 text-white" strokeWidth={1.8} />
              </span>
              <div>
                <p className="font-heading text-3xl font-extrabold leading-none text-white" data-testid="weather-temp-now">
                  {w.temp_now != null ? `${w.temp_now}°` : "\u2014"}
                </p>
                <p className="mt-1 text-sm font-semibold text-white/90" data-testid="weather-description">
                  {w.description}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 text-white/90">
              <span className="inline-flex items-center gap-1 text-sm font-bold">
                <ArrowUp className="h-4 w-4" /> {w.temp_max != null ? `${w.temp_max}°` : "\u2014"}
              </span>
              <span className="inline-flex items-center gap-1 text-sm font-semibold text-white/70">
                <ArrowDown className="h-4 w-4" /> {w.temp_min != null ? `${w.temp_min}°` : "\u2014"}
              </span>
            </div>
          </div>

          {/* 7-day forecast */}
          {days.length > 1 && (
            <div className="border-t border-white/15 bg-white/5 px-2 py-3 sm:px-3">
              <div className="grid grid-cols-7 gap-1" data-testid="weather-forecast">
                {days.map((d, i) => {
                  const DIcon = iconFor(d.code);
                  return (
                    <div
                      key={d.date}
                      className="flex flex-col items-center gap-1.5 rounded-xl px-0.5 py-2 text-white transition-colors hover:bg-white/10"
                      data-testid={`weather-day-${i}`}
                    >
                      <span className="text-[11px] font-bold uppercase tracking-wide text-white/80">
                        {dayLabel(d.date, i)}
                      </span>
                      <DIcon className="h-5 w-5 text-white" strokeWidth={1.8} />
                      <span className="text-xs font-bold leading-tight">{d.temp_max != null ? `${d.temp_max}°` : "\u2014"}</span>
                      <span className="text-xs font-medium leading-tight text-white/60">{d.temp_min != null ? `${d.temp_min}°` : "\u2014"}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default WeatherWidget;
